import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { ImpactCalculator } from "@/components/ImpactCalculator";
import { CertificatePreview } from "@/components/CertificatePreview";

const Shell = ({ eyebrow, title, text, image, children, testId }) => (
  <main data-testid={testId}>
    <section className="relative bg-[#0a1f18] overflow-hidden grain">
      <div className="absolute inset-0 grid-mask" />
      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00f2a1] mb-4">{eyebrow}</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">{title}</h1>
          <p className="mt-5 text-base md:text-lg text-slate-300 max-w-xl">{text}</p>
        </div>
        {image && <img src={image} alt="" className="rounded-3xl h-80 w-full object-cover border border-[#1b3d30]" />}
      </div>
    </section>
    {children}
  </main>
);

const Benefits = ({ items, cta, to, testId }) => (
  <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20">
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
      {items.map(([t, d], i) => (
        <div key={t} className="bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300">
          <span className="font-mono text-xs text-[#00b37a]">0{i + 1}</span>
          <h3 className="font-semibold text-lg text-[#05130e] mt-3 flex gap-2"><CheckCircle2 className="w-5 h-5 text-[#00c487] shrink-0 mt-0.5" />{t}</h3>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d}</p>
        </div>
      ))}
    </div>
    <Link to={to} data-testid={testId} className="mt-12 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#00f2a1] text-[#05130e] font-semibold hover:bg-[#00d890] hover:shadow-[0_0_24px_rgba(0,242,161,0.45)] transition-[background-color,box-shadow] duration-300">
      {cta} <ArrowRight className="w-4 h-4" />
    </Link>
  </section>
);

export const Generadores = () => (
  <Shell testId="empresas-page" eyebrow="Para Empresas Generadoras" image="/img/planta.jpg" title="Tu merma es la materia prima de otra industria"
    text="Publica scrap técnico, descartes y sobrestock. Libera bodega, reduce costos de vertedero y cumple tus metas ESG y Ley REP con trazabilidad por lote.">
    <Benefits to="/publicar" cta="Publicar un lote" testId="empresas-cta"
      items={[["Ingreso o ahorro", "Convierte el gasto de disposición en ingreso por materias primas secundarias."], ["Bodega libre en 48 h", "Subastas rápidas con cierre automático y retiro coordinado."], ["Certificados ESG", "Toneladas valorizadas y CO₂ eq. mitigado por lote para tus reportes."], ["Impacto social", "Dona a liceos técnicos y emprendedores de la economía popular."]]} />
    <section className="max-w-md mx-auto px-5 pb-20"><CertificatePreview /></section>
  </Shell>
);

export const Transformadores = () => (
  <Shell testId="transformadores-page" eyebrow="Para Transformadores / Recicladores" image="/img/polimeros.jpg" title="Materia prima secundaria trazable, cerca de tu planta"
    text="Encuentra lotes de metales, polímeros técnicos, aceites, madera y RAEE con ficha técnica, fotos y certificados de análisis. Oferta en vivo o compra directa.">
    <Benefits to="/marketplace" cta="Explorar el marketplace" testId="transformadores-cta"
      items={[["Ficha técnica clara", "Material, condición, peso y certificados de pureza antes de ofertar."], ["Subastas transparentes", "Ofertas en tiempo real con cuenta regresiva e historial."], ["Filtra por ubicación", "Lotes por región y comuna para optimizar tu logística."], ["Makers y liceos", "Lotes en donación para formación técnica y emprendimiento."]]} />
  </Shell>
);

export const CalculatorPage = () => (
  <Shell testId="calculator-page" eyebrow="Calculadora de Impacto / ESG" title="Mide el valor y el CO₂ evitado de tus mermas"
    text="Selecciona el material y el peso para estimar en tiempo real el ahorro en vertedero, el valor como materia prima secundaria y las toneladas de CO₂ eq. mitigadas.">
    <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16"><ImpactCalculator /></section>
  </Shell>
);
